
import { useState } from "react";
import { usePhotoValidation } from "@/hooks/usePhotoValidation";
import { usePhotoCropper } from "@/hooks/usePhotoCropper";
import { usePhotoUpload } from "@/hooks/usePhotoUpload";
import { toast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface PhotoUploadControllerProps {
  photos: string[];
  onPhotosChange: (photos: string[]) => void;
  maxPhotos?: number;
  children: (props: {
    handleFileSelectWithCrop: (files: FileList | null) => Promise<void>;
    handleEditPhoto: (photoUrl: string, index: number) => void;
    handleDragOver: (e: React.DragEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    isProcessing: boolean;
    canAddMore: boolean;
    uploading: boolean[];
    fileInputRef: React.RefObject<HTMLInputElement>;
    openFileDialog: () => void;
    handleRemovePhoto: (photoUrl: string, index: number) => void;
    cropperProps: {
      cropperOpen: boolean;
      imageToEdit: File | null;
      onClose: () => void;
      onCropComplete: (croppedBlob: Blob, originalFileName: string) => void;
    };
    successModalProps: {
      showSuccessModal: boolean;
      setShowSuccessModal: (show: boolean) => void;
      uploadCount: number;
    };
  }) => React.ReactNode;
}

export const PhotoUploadController = ({ 
  photos, 
  onPhotosChange, 
  maxPhotos = 5, 
  children 
}: PhotoUploadControllerProps) => {
  const [validatingContent, setValidatingContent] = useState(false);
  
  const { validateFile, validateContent } = usePhotoValidation();
  
  const {
    uploading,
    fileInputRef,
    handleFileSelect,
    handleRemovePhoto,
    handleDragDrop,
    openFileDialog,
    showSuccessModal,
    setShowSuccessModal,
    uploadCount
  } = usePhotoUpload({ photos, onPhotosChange, maxPhotos });

  const {
    cropperOpen,
    imageToEdit,
    openCropper,
    closeCropper,
    handleCropComplete
  } = usePhotoCropper({ photos, onPhotosChange, fileInputRef });

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    handleDragDrop(files);
  };

  const handleEditPhoto = (photoUrl: string, index: number) => {
    toast({
      title: "Edit Photo",
      description: "To edit this photo, please remove it and upload a new one with cropping.",
    });
  };

  const handleFileSelectWithCrop = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    
    const file = files[0];
    
    // Validate file format
    if (!(await validateFile(file))) return;

    // Content moderation check
    setValidatingContent(true);
    const isContentValid = await validateContent(file);
    setValidatingContent(false);
    
    if (!isContentValid) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // If this is the first photo (main photo), open cropper for square format
    if (photos.length === 0) {
      openCropper(file, 0);
    } else {
      // For additional photos, upload directly to maintain aspect ratio
      handleFileSelect(files);
    }
  };

  const canAddMore = photos.length + uploading.length < maxPhotos;
  const isProcessing = validatingContent || uploading.some(Boolean);

  return (
    <>
      {children({
        handleFileSelectWithCrop,
        handleEditPhoto,
        handleDragOver,
        handleDrop,
        isProcessing,
        canAddMore,
        uploading,
        fileInputRef,
        openFileDialog,
        handleRemovePhoto,
        cropperProps: {
          cropperOpen,
          imageToEdit,
          onClose: closeCropper,
          onCropComplete: handleCropComplete
        },
        successModalProps: {
          showSuccessModal,
          setShowSuccessModal,
          uploadCount
        }
      })}
    </>
  );
};
